import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectIsOnline,
  selectIsSyncing,
  selectSyncQueueCount,
  selectPendingFileCount,
  selectSyncProgress,
  selectLastSyncedAt,
  selectSyncErrors
} from 'ducks/offline/selectors'
import { triggerSyncRoutine, clearSyncErrorsRoutine, updateSyncCountsRoutine } from 'ducks/offline/actions'
import db from 'services/offlineDb'
import CloseIcon from '@mui/icons-material/Close'
import SyncIcon from '@mui/icons-material/Sync'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CloudOffIcon from '@mui/icons-material/CloudOff'

const TYPE_LABELS = {
  CREATE_TASK: 'Create task',
  UPDATE_TASK: 'Update task',
  DELETE_TASK: 'Delete task',
  SUBMIT_TASK: 'Submit task',
  APPROVE_TASK: 'Approve task',
  REJECT_TASK: 'Reject task',
  HOLD_TASK: 'Hold task',
  RESUME_TASK: 'Resume task',
  SIGN_OFF_TASK: 'Sign off task',
  SAVE_FORM_RESPONSE: 'Save form',
  UPLOAD_FILE: 'Upload file',
  DELETE_FORM_FILE: 'Remove file'
}

const SyncStatusPanel = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const isOnline = useSelector(selectIsOnline)
  const isSyncing = useSelector(selectIsSyncing)
  const syncQueueCount = useSelector(selectSyncQueueCount)
  const pendingFileCount = useSelector(selectPendingFileCount)
  const syncProgress = useSelector(selectSyncProgress)
  const lastSyncedAt = useSelector(selectLastSyncedAt)
  const syncErrors = useSelector(selectSyncErrors)
  const [entries, setEntries] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [loading, setLoading] = useState(false)

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true)
      const all = await db.syncQueue.orderBy('createdAt').toArray()
      setEntries(all)
      const files = await db.pendingFiles.toArray()
      setPendingFiles(files)
    } catch (e) {
      console.error('Failed to load sync entries:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) loadEntries()
  }, [open, syncQueueCount, pendingFileCount, isSyncing, loadEntries])

  const handleSyncNow = () => {
    if (isOnline && !isSyncing) {
      dispatch(triggerSyncRoutine())
    }
  }

  const handleDeleteEntry = async (id) => {
    try {
      await db.syncQueue.delete(id)
      loadEntries()
      dispatch(updateSyncCountsRoutine.trigger())
    } catch (e) {
      console.error('Failed to delete sync entry:', e)
    }
  }

  const handleClearFailed = async () => {
    try {
      await db.syncQueue.where('status').equals('failed').delete()
      await db.pendingFiles.where('status').equals('failed').delete()
      dispatch(clearSyncErrorsRoutine.success())
      dispatch(updateSyncCountsRoutine.trigger())
      loadEntries()
    } catch (e) {
      console.error('Failed to clear failed entries:', e)
    }
  }

  const handleDeleteFile = async (id) => {
    try {
      await db.pendingFiles.delete(id)
      loadEntries()
      dispatch(updateSyncCountsRoutine.trigger())
    } catch (e) {
      console.error('Failed to delete pending file:', e)
    }
  }

  const formatTime = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const diff = now - d
    if (diff < 60000) return 'just now'
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago'
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago'
    return d.toLocaleDateString()
  }

  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlineIcon style={{ color: '#4caf50', fontSize: 18 }} />
      case 'failed': return <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
      case 'syncing': return <SyncIcon style={{ color: '#33becc', fontSize: 18 }} />
      default: return <HourglassEmptyIcon style={{ color: '#ff9800', fontSize: 18 }} />
    }
  }

  const pendingEntries = entries.filter(e => e.status === 'pending' || e.status === 'syncing')
  const failedEntries = entries.filter(e => e.status === 'failed')
  const pendingFilesList = pendingFiles.filter(f => f.status === 'pending')
  const failedFilesList = pendingFiles.filter(f => f.status === 'failed')
  const totalPending = pendingEntries.length + pendingFilesList.length

  if (!open) return null

  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel>
        <Header>
          <HeaderTitle>Sync Status</HeaderTitle>
          <CloseBtn onClick={onClose}><CloseIcon /></CloseBtn>
        </Header>

        <StatusBar>
          {!isOnline ? (
            <StatusRow><CloudOffIcon style={{ color: '#ff9800', fontSize: 18 }} /> <span>You are offline</span></StatusRow>
          ) : isSyncing && syncProgress ? (
            <StatusRow>
              <SyncIcon style={{ color: '#33becc', fontSize: 18 }} />
              <span>{syncProgress.currentItem || 'Syncing...'} ({syncProgress.current}/{syncProgress.total})</span>
            </StatusRow>
          ) : (
            <StatusRow>
              <CheckCircleOutlineIcon style={{ color: '#4caf50', fontSize: 18 }} />
              <span>{lastSyncedAt ? 'Last synced ' + formatTime(lastSyncedAt) : 'Ready'}</span>
            </StatusRow>
          )}
        </StatusBar>

        {isOnline && !isSyncing && (syncQueueCount > 0 || pendingFileCount > 0) && (
          <SyncNowBtn onClick={handleSyncNow}>
            <SyncIcon style={{ fontSize: 18 }} /> Sync now
          </SyncNowBtn>
        )}

        <SectionTitle>
          Pending ({totalPending})
        </SectionTitle>
        {totalPending === 0 ? (
          <EmptyText>No pending changes</EmptyText>
        ) : (
          <EntryList>
            {pendingEntries.map(entry => (
              <EntryRow key={entry.id}>
                <StatusIcon status={entry.status} />
                <EntryInfo>
                  <EntryType>{TYPE_LABELS[entry.type] || entry.type}</EntryType>
                  <EntryTime>{formatTime(entry.createdAt)}</EntryTime>
                </EntryInfo>
                <EntryActions>
                  <SmallBtn onClick={() => handleDeleteEntry(entry.id)} title="Remove">
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </SmallBtn>
                </EntryActions>
              </EntryRow>
            ))}
            {pendingFilesList.map(file => (
              <EntryRow key={'file-' + file.id}>
                <HourglassEmptyIcon style={{ color: '#ff9800', fontSize: 18 }} />
                <EntryInfo>
                  <EntryType>Upload file: {file.fileName}</EntryType>
                  <EntryTime>{formatTime(file.createdAt)}</EntryTime>
                </EntryInfo>
                <EntryActions>
                  <SmallBtn onClick={() => handleDeleteFile(file.id)} title="Remove">
                    <DeleteOutlineIcon style={{ fontSize: 16 }} />
                  </SmallBtn>
                </EntryActions>
              </EntryRow>
            ))}
          </EntryList>
        )}

        {(failedEntries.length > 0 || failedFilesList.length > 0) && (
          <>
            <SectionTitle $error>
              Failed ({failedEntries.length + failedFilesList.length})
              <ClearBtn onClick={handleClearFailed}>Clear all</ClearBtn>
            </SectionTitle>
            <EntryList>
              {failedEntries.map(entry => (
                <EntryRow key={entry.id} $failed>
                  <StatusIcon status={entry.status} />
                  <EntryInfo>
                    <EntryType>{TYPE_LABELS[entry.type] || entry.type}</EntryType>
                    <EntryError>{entry.lastError || 'Unknown error'}</EntryError>
                    <EntryTime>{formatTime(entry.createdAt)}</EntryTime>
                  </EntryInfo>
                  <EntryActions>
                    <SmallBtn onClick={() => handleDeleteEntry(entry.id)} title="Remove">
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </SmallBtn>
                  </EntryActions>
                </EntryRow>
              ))}
              {failedFilesList.map(file => (
                <EntryRow key={'file-' + file.id} $failed>
                  <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
                  <EntryInfo>
                    <EntryType>Upload file: {file.fileName}</EntryType>
                    <EntryError>{file.lastError || 'Upload failed'}</EntryError>
                    <EntryTime>{formatTime(file.createdAt)}</EntryTime>
                  </EntryInfo>
                  <EntryActions>
                    <SmallBtn onClick={() => handleDeleteFile(file.id)} title="Remove">
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </SmallBtn>
                  </EntryActions>
                </EntryRow>
              ))}
            </EntryList>
          </>
        )}

        {syncErrors.length > 0 && (
          <>
            <SectionTitle $error>Sync Errors</SectionTitle>
            <EntryList>
              {syncErrors.map((err, i) => (
                <EntryRow key={i} $failed>
                  <ErrorOutlineIcon style={{ color: '#f44336', fontSize: 18 }} />
                  <EntryInfo>
                    <EntryType>{TYPE_LABELS[err.type] || err.type}</EntryType>
                    <EntryError>{err.error}</EntryError>
                  </EntryInfo>
                </EntryRow>
              ))}
            </EntryList>
          </>
        )}
      </Panel>
    </>
  )
}

export default SyncStatusPanel

const Backdrop = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3);
  z-index: 1300;
`
const Panel = styled.div`
  position: fixed;
  top: 0; right: 0; bottom: 0;
  width: 360px;
  max-width: 90vw;
  background: #fff;
  z-index: 1301;
  box-shadow: -4px 0 20px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
`
const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
`
const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  display: flex;
  &:hover { color: #333; }
`
const StatusBar = styled.div`
  padding: 12px 20px;
  background: #f8f9fc;
  border-bottom: 1px solid #eee;
`
const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
`
const SyncNowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 12px 20px;
  padding: 10px 16px;
  background: #33becc;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #2aa8b8; }
`
const SectionTitle = styled.div`
  padding: 12px 20px 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $error }) => $error ? '#f44336' : '#999'};
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const ClearBtn = styled.button`
  background: none;
  border: none;
  color: #f44336;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: #d32f2f; }
`
const EmptyText = styled.div`
  padding: 8px 20px 16px;
  font-size: 13px;
  color: #aaa;
`
const EntryList = styled.div`
  padding: 0 12px 8px;
`
const EntryRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 8px;
  background: ${({ $failed }) => $failed ? '#fff5f5' : 'transparent'};
  &:hover { background: ${({ $failed }) => $failed ? '#ffebee' : '#f5f5f5'}; }
`
const EntryInfo = styled.div`
  flex: 1;
  min-width: 0;
`
const EntryType = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #333;
`
const EntryTime = styled.div`
  font-size: 11px;
  color: #aaa;
  margin-top: 2px;
`
const EntryError = styled.div`
  font-size: 11px;
  color: #f44336;
  margin-top: 2px;
`
const EntryActions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`
const SmallBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #999;
  display: flex;
  border-radius: 4px;
  &:hover { background: #eee; color: #666; }
`
